import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Square, Upload, X } from 'lucide-react';

const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024;

const formatTime = (seconds = 0) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const buildPreviewUrl = (file) => (file ? URL.createObjectURL(file) : '');

const createRecordedFile = (blob, mimeType) => {
  const extension = mimeType?.includes('mp4') ? 'm4a' : mimeType?.includes('ogg') ? 'ogg' : 'webm';
  return new File([blob], `document-narration-${Date.now()}.${extension}`, {
    type: mimeType || 'audio/webm',
  });
};

const isAudioFile = (file) => {
  return !!file && (
    file.type?.startsWith('audio/') ||
    /\.(mp3|wav|m4a|aac|ogg|webm)$/i.test(file.name || '')
  );
};

const DocumentNarrationModal = ({
  isOpen,
  isSaving = false,
  maxDuration = 300,
  title = 'Add Document Narration',
  description = 'Record a narration or upload an audio file for this document.',
  saveLabel = 'Save Narration',
  onClose,
  onSave,
}) => {
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  const [mode, setMode] = useState('record');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');

  const previewUrl = useMemo(() => buildPreviewUrl(audioFile), [audioFile]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setMode('record');
      setIsRecording(false);
      setRecordingTime(0);
      setAudioFile(null);
      setDuration(0);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const stopActiveStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  };

  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';

      recorderRef.current = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      setRecordingTime(0);
      setAudioFile(null);
      setDuration(0);

      recorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorderRef.current.onstop = () => {
        const nextMimeType = recorderRef.current?.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: nextMimeType });
        const file = createRecordedFile(blob, nextMimeType);
        setAudioFile(file);
        setDuration(recordingTime || 0);
        stopActiveStream();
      };

      recorderRef.current.start(200);
      setIsRecording(true);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const next = prev + 1;
          if (next >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return next;
        });
      }, 1000);
    } catch (recordError) {
      console.error('Narration recording failed:', recordError);
      setError('Microphone access failed. Check browser permissions and try again.');
      stopActiveStream();
    }
  };

  const loadAudioMetadata = (file) => {
    const audio = document.createElement('audio');
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      setDuration(Number(audio.duration) || 0);
      URL.revokeObjectURL(objectUrl);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
    };
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isAudioFile(file)) {
      setError('Choose an audio file in MP3, WAV, M4A, OGG, or WebM format.');
      return;
    }
    if (file.size > MAX_AUDIO_SIZE_BYTES) {
      setError('Audio files must be under 50 MB.');
      return;
    }
    setError('');
    setAudioFile(file);
    loadAudioMetadata(file);
  };

  const handleSubmit = async () => {
    if (!audioFile) {
      setError('Record or upload an audio file first.');
      return;
    }
    setError('');
    await onSave({
      file: audioFile,
      duration,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setMode('record')}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'record' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Record
            </button>
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'upload' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Upload
            </button>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {mode === 'record' && !audioFile && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-8 text-center">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                {isRecording ? <Square className="h-10 w-10" /> : <Mic className="h-10 w-10" />}
              </div>
              <div className="mt-5">
                <p className="text-3xl font-semibold tracking-tight text-gray-900">{formatTime(recordingTime)}</p>
                <p className="mt-1 text-sm text-gray-500">Maximum length: {formatTime(maxDuration)}</p>
              </div>
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                className={`mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-colors ${
                  isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {isRecording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </button>
            </div>
          )}

          {mode === 'upload' && !audioFile && (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <Upload className="h-10 w-10" />
              </div>
              <p className="mt-5 text-lg font-medium text-gray-900">Upload audio narration</p>
              <p className="mt-1 text-sm text-gray-500">MP3, WAV, M4A, OGG, or WebM up to 50 MB.</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                <Upload className="h-4 w-4" />
                Choose Audio File
              </button>
            </div>
          )}

          {audioFile && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{audioFile.name}</p>
                  <p className="text-xs text-gray-500">{formatTime(duration)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAudioFile(null);
                    setDuration(0);
                    setRecordingTime(0);
                    setError('');
                  }}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900"
                >
                  Replace
                </button>
              </div>
              <audio controls src={previewUrl} className="w-full" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!audioFile || isSaving}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentNarrationModal;
